import { Component, OnDestroy, OnInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { BrokenLinksUtil } from '@client/app/pages/qa/broken-links/broken-links.util';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { QABrokenLinkService } from '@client/app/blocks/service/qa/broken-link.service';
import { LinksRepoService } from '@client/app/services/api/links-repo/links-repo.service';
import { LabelRepo } from '@monsido/modules/endpoints/api/label.repo';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'broken-sitemap-index-component',
    template: `
        @let totalLinks = links.total | monNumber;
        <page-header
                icon="{{ ['QA', 'BROKEN_SITEMAP'] | monIcons }}"
                header="{{ 'Broken Sitemap Links' | translate }}"
                subHeader="<strong>{{ totalLinks }}</strong> {{ 'link' | translateWithPlural: links.total:'links'}}"
        >
        </page-header>
        <mon-tabs
                [showSearch]="true"
                [options]="tabs"
                [(status)]="activeTab"
                (onStatus)="onStatus($event)"
                (onSearch)="onSearch($event)"
                [search]="search"
                [minSearchLength]="1"
                [showFiltersBtn]="true"
                [(isFiltersOpen)]="isFiltersOpen"
        >
            <csv-export-button [exports]="exports"></csv-export-button>
        </mon-tabs>
        <div class="card panel-monsido-card">
            <div class="card-body">
                <div class="row">
                    <div class="col-md-48 col-lg-48 col-48">
                        <table-container-top>
                            <div *ngIf="isFiltersOpen">
                                <table-filters
                                        [(open)]="isFiltersOpen"
                                        [(ngModel)]="filters"
                                        [filters]="availableFilters"
                                        (onChanges)="updateFilters($event)"
                                >
                                </table-filters>
                            </div>
                        </table-container-top>
                        <broken-table [selected]="selected"
                                      (getPage)="getPage()"
                                      [items]="links"
                                      (onPageChange)="onPageChange($event)"
                                      (onPerPageChange)="onPageSizeChange($event)"
                                      (onSortPage)="onSortPage($event.direction, $event.by)"
                                      [currentSort]="currentSort"
                                      [type]="activeTab"
                                      [progress]="promise"
                                      [labels]="labels"
                                      [selectedLabels]="selectedLabels"
                                      (getLabels)="getLabels()"
                                      [hidePagesColumn]="true">
                        </broken-table>
                    </div>
                </div>
            </div>
        </div>

    `,
    standalone: false,
})

export class BrokenSitemapIndexComponent extends BaseContainerComponentController implements OnInit, OnDestroy {
    tabs: any[] = [];
    links: any = [];
    selected: any[] = [];
    type: string = 'not_image';
    override isFiltersOpen = false;
    qaBrokenSitemapUpdateListener: any;
    override filters: any;
    override availableFilters: any = {};
    exports: any[] = [];
    override selectedLabels: any[] = [];
    promise: any;
    override labels: any[] = [];
    override activeTab: string = 'dead';
    override search: string = '';
    override currentSort: any = { by: 'url', direction: 'asc' };

    constructor (
        private translateService: TranslateService,
        private ng2MonEventsService: MonEventService,
        private qaBrokenLinkService: QABrokenLinkService,
        private ng2LinksRepoService: LinksRepoService,
        private ng2LabelRepo: LabelRepo,
    ) {
        super({ table: true, filters: true, labels: true });
    }

    ngOnInit (): void {
        this.selected = [];
        this.search = '';
        this.links = [];
        this.tabs = [
            { name: this.translateService.getString('All broken links'), faIcon: 'fas fa-unlink', value: 'dead' },
            { name: this.translateService.getString('Ignored'), faIcon: 'fas fa-eye-slash', value: 'ignored' },
            { name: this.translateService.getString('Marked as fixed'), faIcon: 'fas fa-check-circle', value: 'fixed' },
        ];
        this.activeTab = 'dead';

        this.qaBrokenSitemapUpdateListener = this.ng2MonEventsService.addListener(MON_EVENTS.RELOAD_QA_BROKEN_SITEMAP, () => {
            this.getPage();
        });

        this.filters = BrokenLinksUtil.getSitemapFilters();
        this.availableFilters = {
            labels: [],
            type: [
                { id: 'html', name: this.translateService.getString('Pages') },
                { id: 'document', name: this.translateService.getString('Documents') },
            ],
            'response code': this.qaBrokenLinkService.getResponseCodes(),
            'internal or external': [
                { id: 'internal', name: this.translateService.getString('Internal') },
                { id: 'external', name: this.translateService.getString('External') },
            ],
        };
        this.currentSort = { by: 'url', direction: 'asc' };

        this.exports = [
            {
                name: this.translateService.getString('Broken Sitemap Links'),
                data: {
                    category: 'dead_sitemap_links',
                },
            },
        ];

        this.getLabels();
        this.getPage();
    }

    ngOnDestroy () {
        this.ng2MonEventsService.remove(this.qaBrokenSitemapUpdateListener);
    }

    override getPage = () => {
        this.selected = [];
        const params: any = {
            page: this.page,
            page_size: this.pageSize,
            search: this.search ?? '',
            status: this.activeTab,
            on_sitemap: true,
            type: this.type,
            sort_by: this.currentSort.by,
            sort_dir: this.currentSort.direction,
            page_fixed: false,
        };
        if (this.selectedLabels && this.selectedLabels.length > 0) {
            params['labels[]'] = this.selectedLabels;
        }
        if (this.filters.type.length) {
            params.page_type = this.filters.type[0].id;
        }
        if (this.filters['response code'].length) {
            params.status_code = this.filters['response code'][0].id;
        }
        if (this.filters['internal or external'].length) {
            params.relation = this.filters['internal or external'][0].id;
        }

        this.promise = this.ng2LinksRepoService.getAll(params).then((data) => {
            this.links = data;
        }, () => {});
    };

    override updateFilters (changes) {
        if (!changes) {
            return;
        }

        this.selectedLabels = [];
        if (changes.labels.length > 0) {
            for (const label of changes.labels) {
                this.selectedLabels.push(label.id);
            }
        }

        this.filters = BrokenLinksUtil.updateLinksFilters(this.filters, changes);
        this.getPage();
    }

    override onStatus (tab) {
        this.activeTab = tab;
        this.getPage();
    }

    override onSearch (search) {
        this.search = search;
        this.getPage();
    }
}
